import {toast} from 'react-toastify';
import {FC, useEffect, useRef, useState} from 'react';
import {VerdocsButton, VerdocsSpinner} from '@verdocs/web-sdk-react';
import {type IBrand, createBrand, VerdocsEndpoint} from '@verdocs/js-sdk';
import {BRAND_KEY_PATTERN, contrastText, slugifyKey} from '../Views/Settings/BrandThemes';
import {track} from '../Managers/Analytics';
import {AppState} from '../AppState';
import {CONFIG} from '../config';
import {Dialog} from './Dialog';

const STEPS = ['Reading site metadata', 'Finding your logo', 'Sampling brand colors', 'Building a toolbar-safe palette', 'Assembling your brand'];

interface IPreview {
  domain: string;
  name: string;
  initials: string;
  logoUrl: string | null;
  logoSource: string | null;
  colors: {primary: string; primaryDark: string; accent: string; tint: string; toolbarText: string};
  links: {url: string | null; termsUrl: string | null; privacyPolicyUrl: string | null};
}

// Response contract of the workflows worker's POST /brand-preview. Keep in sync with
// the worker (experiments/workers/verdocs-workflows/src/demo/brand-preview.ts).
interface IPreviewResponse {
  ok: boolean;
  error?: string;
  message?: string;
  domain?: string;
  brand?: {name: string; initials: string};
  logo?: {url: string | null; source: string | null};
  colors?: {primary: string; primaryDark: string; accent: string; tint: string; toolbarText: string; source?: string};
  /** Footer material read from the site: its canonical URL and any terms and privacy pages it links to. */
  links?: {url: string | null; termsUrl: string | null; privacyPolicyUrl: string | null};
}

interface MatchWebsiteDialogProps {
  onClose: () => void;
  onCreated: (brand: IBrand) => void;
  onSwitchToThemes: () => void;
}

export const MatchWebsiteDialog: FC<MatchWebsiteDialogProps> = ({onClose, onCreated, onSwitchToThemes}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [url, setUrl] = useState('');
  const [phase, setPhase] = useState<'idle' | 'fetching' | 'ready'>('idle');
  const [stepIdx, setStepIdx] = useState(0);
  const [fetchError, setFetchError] = useState('');
  const [preview, setPreview] = useState<IPreview | null>(null);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => stopTicker(), []);

  const stopTicker = () => {
    if (ticker.current) clearInterval(ticker.current);
    ticker.current = null;
  };

  const handleFetch = async () => {
    const trimmed = url.trim();
    if (!trimmed || phase === 'fetching') return;
    setPhase('fetching');
    setFetchError('');
    setPreview(null);
    setStepIdx(0);
    ticker.current = setInterval(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), 700);

    try {
      const res = await fetch(`${CONFIG.WORKFLOWS_URL}/brand-preview`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({url: trimmed, token: ''}),
      });
      const data = (await res.json()) as IPreviewResponse;
      stopTicker();
      if (data.ok && data.domain && data.brand && data.colors) {
        setPreview({
          domain: data.domain,
          name: data.brand.name,
          initials: data.brand.initials,
          logoUrl: data.logo?.url ?? null,
          logoSource: data.logo?.source ?? null,
          colors: data.colors,
          links: data.links ?? {url: null, termsUrl: null, privacyPolicyUrl: null},
        });
        setName(data.brand.name);
        setKey(slugifyKey(data.domain.split('.')[0]));
        setPhase('ready');
      } else {
        setFetchError(data.message || "We couldn't read that site. Please check the URL and try again.");
        setPhase('idle');
      }
    } catch {
      stopTicker();
      setFetchError("We couldn't reach the brand service. Please try again in a moment.");
      setPhase('idle');
    }
  };

  const reset = () => {
    stopTicker();
    setPhase('idle');
    setPreview(null);
    setFetchError('');
  };

  const canCreate = phase === 'ready' && !!preview && BRAND_KEY_PATTERN.test(key) && !submitting;

  const handleCreate = () => {
    if (!preview || !canCreate) return;
    setSubmitting(true);
    createBrand(VerdocsEndpoint.getDefault(), organizationId, {
      key,
      name: name.trim() || undefined,
      email_sender_name: (name.trim() || preview.name).substring(0, 120) || undefined,
      full_logo_url: preview.logoUrl ?? undefined,
      thumbnail_url: preview.logoUrl ?? undefined,
      primary_color: preview.colors.primary,
      secondary_color: preview.colors.accent,
      // The footer starts from what the site told us; all of it is editable in the Builder.
      powered_by_label: `Powered by ${name.trim() || preview.name}`,
      powered_by_url: preview.links.url || `https://${preview.domain}`,
      terms_use_url: preview.links.termsUrl ?? undefined,
      privacy_policy_url: preview.links.privacyPolicyUrl ?? undefined,
    })
      .then((r) => {
        setSubmitting(false);
        toast.success('Brand created');
        track('brand_created', {org_id: organizationId, source: 'website'});
        onCreated(r);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating brand: ${e.response?.data?.error || e.message}`);
      });
  };

  const swatches = preview
    ? [
        {label: 'Primary', color: preview.colors.primary},
        {label: 'Primary Dark', color: preview.colors.primaryDark},
        {label: 'Accent', color: preview.colors.accent},
        {label: 'Tint', color: preview.colors.tint},
      ]
    : [];

  return (
    <Dialog onCancel={onClose}>
      <div className="max-h-[80vh] overflow-y-auto text-base">
        <div className="mb-3 flex items-center">
          <h1 className="sans-serif flex-1 text-xl font-bold text-[#33364b]">Match my Web site</h1>
          <button onClick={onClose} className="rounded-md px-2 py-0.5 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            &times;
          </button>
        </div>
        <p className="mb-4 text-[13.5px] leading-relaxed text-gray-500">
          We'll read your public site and assemble a starting brand: name, logo, and a toolbar-safe color set. You can change everything
          afterward.
        </p>

        <div className="mb-2 flex gap-2.5">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetch()}
            placeholder="acmesure.com"
            spellCheck={false}
            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
          <VerdocsButton size="small" label="Fetch my brand" disabled={phase === 'fetching' || !url.trim()} onClick={handleFetch} />
        </div>
        <p className="text-xs text-gray-400">Your site must be publicly accessible.</p>
        {fetchError && <p className="mt-3 text-sm text-red-600">{fetchError}</p>}

        {phase === 'fetching' && (
          <div className="mt-4 rounded-xl border border-gray-200 px-5 py-4">
            {STEPS.map((step, i) => (
              <div key={step} className={`flex items-center gap-3 py-1.5 text-[13.5px] ${i <= stepIdx ? 'text-gray-900' : 'text-gray-400'}`}>
                {i < stepIdx ? (
                  <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-emerald-500 text-[11px] text-white">✓</span>
                ) : i === stepIdx ? (
                  <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                ) : (
                  <span className="h-[18px] w-[18px] rounded-full border-2 border-gray-200" />
                )}
                {step}
              </div>
            ))}
          </div>
        )}

        {phase === 'ready' && preview && (
          <>
            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
              <div className="flex items-center gap-3.5 border-b border-gray-100 px-4 py-4">
                {preview.logoUrl ? (
                  <img src={preview.logoUrl} alt="" className="h-12 w-12 rounded-xl bg-gray-50 object-contain" />
                ) : (
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl text-lg font-extrabold text-white"
                    style={{background: preview.colors.primary}}>
                    {preview.initials}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <b className="block text-[15px] text-gray-900">{preview.name}</b>
                  <span className="text-[12.5px] text-gray-500">{preview.domain}</span>
                </div>
                {preview.logoSource === 'brandfetch' && (
                  <span className="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-gray-400">Colors &amp; logo via Brandfetch</span>
                )}
              </div>
              <div className="px-4 py-4">
                <div className="mb-4 flex flex-wrap gap-2.5">
                  {swatches.map((sw) => (
                    <div key={sw.label} className="text-center">
                      <i className="mb-1 block h-9 w-[72px] rounded-lg border border-black/10" style={{background: sw.color}} />
                      <b className="block text-[10.5px] font-semibold text-gray-700">{sw.label}</b>
                      <span className="font-mono text-[10px] text-gray-400">{sw.color}</span>
                    </div>
                  ))}
                </div>
                <div
                  className="mb-3 flex items-center gap-2.5 rounded-lg px-3 py-2"
                  style={{background: preview.colors.primaryDark, color: preview.colors.toolbarText}}>
                  <span className="flex-1 text-[12.5px] font-semibold">Agreement.pdf</span>
                  <span
                    className="rounded px-3 py-1.5 text-[11px] font-bold"
                    style={{background: preview.colors.accent, color: contrastText(preview.colors.accent)}}>
                    Next Required
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-gray-500">
                  This is how your signing toolbar would look. We darken the primary for chrome and check text contrast automatically, so fetched
                  brands never produce an unreadable toolbar.
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Brand name"
                className="w-44 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none"
              />
              <input
                value={key}
                onChange={(e) => setKey(slugifyKey(e.target.value))}
                placeholder="key"
                className="w-32 rounded-lg border border-gray-300 px-2.5 py-1.5 font-mono text-sm focus:border-indigo-400 focus:outline-none"
              />
              <div className="flex-1" />
              <button onClick={reset} className="px-1 text-sm font-medium text-indigo-600 hover:underline">
                Try another URL
              </button>
            </div>
          </>
        )}

        {phase === 'idle' && (
          <p className="mt-4 text-xs leading-relaxed text-gray-500">
            We do our best to identify your branding from your site. If your site code doesn't expose enough details, we'll provide a sensible
            starting point to refine.
          </p>
        )}

        <div className="mt-6 flex flex-row items-center justify-end gap-4">
          <VerdocsButton size="small" variant="outline" label="Pick a theme instead" onClick={onSwitchToThemes} disabled={submitting} />
          <VerdocsButton size="small" label="Create Brand" disabled={!canCreate} onClick={handleCreate} />
          {submitting && <VerdocsSpinner mode="dark" />}
        </div>
      </div>
    </Dialog>
  );
};
